import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Sprout } from 'lucide-react';
import styles from './EmailCaptureModal.module.css';

type ModalState = 'idle' | 'loading' | 'success' | 'error';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mpqybknv';
const WAITLIST_SOURCE = 'green_rituals_waitlist';

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

interface EmailCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EmailCaptureModal({ isOpen, onClose }: EmailCaptureModalProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [email, setEmail] = useState('');
  const [state, setState] = useState<ModalState>('idle');
  const [touched, setTouched] = useState(false);

  const valueBullets = [
    '$49 · Free NYC delivery',
    'Priority access before anyone else',
    'First batch — 200 kits only',
  ];

  useEffect(() => {
    if (!isOpen) return;

    setEmail('');
    setState('idle');
    setTouched(false);

    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  const hasValidEmail = useMemo(() => /\S+@\S+\.\S+/.test(email.trim()), [email]);
  const shouldShowError = touched && !hasValidEmail;

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    setTouched(true);

    if (!hasValidEmail || state === 'loading') return;

    const normalizedEmail = email.trim();
    setState('loading');

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail,
          source: WAITLIST_SOURCE,
        }),
      });

      if (!response.ok) {
        setState('error');
        return;
      }

      const extractedEmailDomain = normalizedEmail.split('@')[1]?.toLowerCase() ?? '';

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'waitlist_signup',
        form_name: WAITLIST_SOURCE,
        form_location: 'popup',
        email_domain: extractedEmailDomain,
      });

      setEmail('');
      setTouched(false);
      setState('success');
    } catch {
      setState('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
          ×
        </button>

        {state === 'success' ? (
          <div className={styles.successWrap}>
            <div className={styles.successIconWrap} aria-hidden="true">
              <Sprout strokeWidth={1.8} />
            </div>
            <h3 id="email-modal-title" className={styles.title}>
              You're In
            </h3>
            <p className={styles.text}>
              We'll send your checkout link before anyone else.
              <br />
              Watch your inbox — we launch soon.
            </p>
            <button type="button" className={styles.submitButton} onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className={styles.headlineWrap}>
              <div className={styles.priceBubble}>$49</div>
              <h3 id="email-modal-title" className={styles.title}>
                Secure Your Spot
              </h3>
            </div>
            <p className={styles.text}>
              First batch: 200 kits across NYC. Drop your email and we'll send
              you the checkout link the moment we launch.
            </p>

            <ul className={styles.valueList}>
              {valueBullets.map((bullet) => (
                <li key={bullet} className={styles.valueItem}>
                  <Check className={styles.valueIcon} strokeWidth={2} aria-hidden="true" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>

            <form className={styles.form} onSubmit={onSubmit} noValidate>
              <label htmlFor="email-input" className={styles.label}>
                Your Email
              </label>
              <input
                ref={inputRef}
                id="email-input"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onBlur={() => setTouched(true)}
                onChange={(event) => setEmail(event.target.value)}
                className={`${styles.input} ${shouldShowError ? styles.inputError : ''}`.trim()}
                placeholder="you@example.com"
                aria-invalid={shouldShowError}
                aria-describedby={shouldShowError || state === 'error' ? 'email-error' : undefined}
              />
              {shouldShowError && (
                <p id="email-error" className={styles.error}>
                  Please enter a valid email address.
                </p>
              )}
              {state === 'error' && (
                <p id="email-error" className={styles.error}>
                  Something went wrong. Please try again.
                </p>
              )}

              <button type="submit" className={styles.submitButton} disabled={state === 'loading'}>
                {state === 'loading' ? 'Reserving...' : 'Reserve My Spot — $49'}
              </button>
              <p className={styles.microtext}>No credit card. No spam. Unsubscribe any time.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
